/*
    Requires global-helper.js for compression and fetching data from API.
*/

class ClientSideProductStorage {

    /*
        onProductsReady is called when this.products is populated and available.
        onProductsUpdated is called if this.products was updated by a new fetch request (if version number is wrong or 30 minutes have ellapsed)
    */
    constructor(onProductsReady = (products) => {}, onProductsUpdated = (products) => {}) {

        // Static constants
        this.localStorageKey = "clientProductsStorage";
        this.productsFormatVersion = 1;
        this.minutesUntilNextUpdate = 30;

        this.products = new Map();
        this.onProductsReady = onProductsReady;
        this.onProductsUpdated = onProductsUpdated;
        // Update immediately in case a fetch is required.
        this.populateProducts();

    }

    /*
        Populates this.products from either the local cache or new fetch from server endpoint.
    */
    async populateProducts() {

        let storedProducts = this._getStoredProductsJSON();
        let hadUpdate = false;

        if (storedProducts === null) {
            
            await this._fetchAndPopulateProducts();
            storedProducts = this._getStoredProductsJSON();

        }

        if (this._shouldFetch(storedProducts)) {
            
            await this._fetchAndPopulateProducts();
            storedProducts = this._getStoredProductsJSON();
            hadUpdate = true;

        }

        this.products = new Map(JSON.parse(await decompressStringWithGZIP(storedProducts.products)).products.map(product => [product.id, product]));
        this.onProductsReady(this.products);

        if (hadUpdate) {

            this.onProductsUpdated(this.products);

        }

    }

    /*
        Updates products with information from products API page.
        TODO: At some point make custom app for .json endpoint as this solution is kind of janky.
    */
    async _fetchAndPopulateProducts() {

        try {

            const productsJSON = await fetchJSONFromAPIPage("products");

            await this._setProducts(productsJSON);

        } catch (error) {

            console.error("Error updating products from API.", error);
            this.products = new Map();

        }

    }

    /*
        Return true or false depending on whether the products should be updated with a new API fetch.
    */
    _shouldFetch(storedProductsJSON) {

        // Check if format versions differ.
        if (storedProductsJSON.formatVersion !== this.productsFormatVersion) {

            return true;

        }

        // Check if 30 minutes have passed since last fetch.
        if (Math.floor(Date.now() - storedProductsJSON.timestamp) / 60000 > this.minutesUntilNextUpdate) {

            return true;

        }

        return false;

    }

    /*
        Sets this.products and updates localStorage with the provided products in JSON.
    */
    async _setProducts(json) {

        const compressedProducts = await compressStringWithGZIP(JSON.stringify(json));
        this.products = new Map(json.products.map(product => [product.id, product]));
        
        localStorage.setItem(this.localStorageKey, JSON.stringify({
            products: compressedProducts,
            formatVersion: this.productsFormatVersion,
            timestamp: Date.now()
        }));

    }

    _getStoredProductsJSON() {

        try {

            return JSON.parse(localStorage.getItem(this.localStorageKey));

        } catch (error) {

            console.error("Error when parsing locally stored products:", error);
            return null;

        }

    }

}